import { personRef } from "@/content/author";
import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import { PageIntro, PageSection, MoreLink } from "@/components/site/page";

const URL = "https://akillisurdurulebilirlik.com/yazilar/greenwashing-nedir";
const TITLE = "Greenwashing Nedir? — Yeşil Aklamayı Tanıma Rehberi";
const DESC =
  "Greenwashing (yeşil aklama) nedir, nasıl tanınır, neden yaygınlaşıyor? Tipik kalıplar, gerçek örnekler ve tüketici ile profesyonel için pratik bir kontrol listesi.";
const PUBLISHED = "2026-06-11";

const FAQ = [
  {
    q: "Greenwashing nedir, kısaca?",
    a: "Greenwashing — Türkçesi 'yeşil aklama' — bir şirketin, ürünün veya kurumun sürdürülebilirlik söylemini bir pazarlama katmanı olarak kullanması, ama altyapısını, tedarik zincirini ya da üretim hacmini değiştirmemesidir. Görüntü yeşildir, sistem aynıdır.",
  },
  {
    q: "Yeşil aklama nasıl tanınır?",
    a: "Beş kalıp yaygındır: belirsiz dil ('doğa dostu', 'sürdürülebilir kaynaklı'), ölçülemez vaatler ('2050'de net sıfır'), ürünün küçük bir yönünü öne çıkarıp asıl etkisini gizleme, sertifikaların kaynağını belirtmeme ve karbon dengeleme sertifikalarına aşırı güvenme.",
  },
  {
    q: "Greenwashing yasal mı?",
    a: "Çoğu ülkede yanıltıcı reklam mevzuatına girer. AB 2024'te Green Claims Direktifi'ni kabul etti; çevresel iddialar artık bağımsız doğrulama gerektiriyor. Türkiye'de Reklam Kurulu da 'yeşil iddialara' ilişkin kararlar veriyor — ama denetim hâlâ zayıf.",
  },
  {
    q: "Greenwashing ile gerçek sürdürülebilirlik arasındaki fark nedir?",
    a: "Greenwashing iletişim katmanında kalır; gerçek sürdürülebilirlik enerji karmasını, üretim hacmini, tedarik zincirini ve atık politikasını değiştirir. Ölçütü basittir: şirketin sürdürülebilirlik raporu çıkarılmadan önce kararlar değişti mi, yoksa kararlar aynı kalıp rapor mu yazıldı?",
  },
  {
    q: "Bir tüketici olarak greenwashing'i nasıl ayırt ederim?",
    a: "Üç soru sorun: (1) Etiketteki iddianın arkasında bağımsız bir sertifika var mı, kim veriyor? (2) İddia ürünün hangi yüzdesini kapsıyor — örneğin 'geri dönüştürülmüş plastik' ürünün %5'i mi yoksa %80'i mi? (3) Şirketin toplam karbon emisyonu son üç yılda azaldı mı, yoksa sadece pazarlama bütçesi mi büyüdü?",
  },
];

export const Route = createFileRoute("/yazilar/greenwashing-nedir")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "greenwashing nedir, yeşil aklama, greenwashing örnekleri, yeşil aklama nedir, sürdürülebilirlik eleştirisi, ESG, karbon dengeleme, çevreci pazarlama, ekolojik simülasyon, washing ekonomisi",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL },
      ...hreflangLinks("/yazilar/greenwashing-nedir"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          inLanguage: "tr-TR",
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: personRef(),
          publisher: {
            "@type": "Organization",
            name: "Akıllı Sürdürülebilirlik",
            url: "https://akillisurdurulebilirlik.com",
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": URL },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://akillisurdurulebilirlik.com/" },
            { "@type": "ListItem", position: 2, name: "Yazılar", item: "https://akillisurdurulebilirlik.com/yazilar" },
            { "@type": "ListItem", position: 3, name: "Greenwashing Nedir?", item: URL },
          ],
        }),
      },
    ],
  }),
  component: GreenwashingPage,
});

function GreenwashingPage() {
  return (
    <>
      <PageIntro
        crumbs={[{ label: "Yazılar", to: "/yazilar" }, { label: "Greenwashing Nedir?" }]}
        kicker="Rehber"
        title="Greenwashing nedir?"
        lede={
          <>
            Türkçesi "yeşil aklama". Bir şirketin, ürünün ya da kurumun sürdürülebilirlik
          söylemini pazarlama katmanı olarak kullanması, ama altyapıyı değiştirmemesi.
          Bu rehber, kalıpları, gerçek örnekleri ve nasıl tanıyacağınızı anlatıyor.
          </>
        }
      />
      <PageSection>
        <article className="max-w-3xl">

      <nav className="mb-12 border border-ink/10 p-5 md:p-6 bg-secondary" aria-label="İçindekiler">
        <h2 className="text-[10px] uppercase tracking-[0.25em] font-bold text-ink/60 mb-3">
          İçindekiler
        </h2>
        <ol className="space-y-1.5 text-sm">
          <li><a href="#tanim" className="hover:text-theme-tech">1. Greenwashing'in kısa tanımı</a></li>
          <li><a href="#kokeni" className="hover:text-theme-tech">2. Terim nereden geldi</a></li>
          <li><a href="#kaliplar" className="hover:text-theme-tech">3. Yedi tipik kalıp</a></li>
          <li><a href="#ornekler" className="hover:text-theme-tech">4. Gerçek dünyadan örnekler</a></li>
          <li><a href="#neden" className="hover:text-theme-tech">5. Neden bu kadar yaygın?</a></li>
          <li><a href="#kontrol-listesi" className="hover:text-theme-tech">6. Tüketici kontrol listesi</a></li>
          <li><a href="#profesyonel" className="hover:text-theme-tech">7. Profesyonel için: nasıl kaçınılır?</a></li>
          <li><a href="#ai" className="hover:text-theme-tech">8. AI çağında yeni greenwashing</a></li>
          <li><a href="#sss" className="hover:text-theme-tech">9. Sıkça sorulanlar</a></li>
        </ol>
      </nav>

      <div className="prose-content space-y-6 text-base md:text-lg leading-[1.75] text-ink/85">
        <section id="tanim" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            1. Greenwashing'in kısa tanımı
          </h2>
          <p>
            Greenwashing, bir şirketin, kurumun ya da hükûmetin sürdürülebilirlik
            iddiasını <strong>gerçek değişiklikten daha hızlı</strong> üretmesidir.
            Yeşil etiket, üretim hacminden, tedarik zincirinden ve enerji karmasından
            önce çıkar. Görüntü değişir, sistem aynı kalır.
          </p>
          <p>
            Türkçesi "yeşil aklama". Sözcük bilinçli olarak kara para "aklama" fiiliyle
            akrabadır: bir şeyi temizmiş gibi göstermek, izini gizlemek. Aradaki fark,
            burada aklanan şeyin para değil <em>itibar ve karar meşruiyeti</em> olmasıdır.
          </p>
        </section>

        <section id="kokeni" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            2. Terim nereden geldi
          </h2>
          <p>
            Terimi 1986'da çevreci yazar Jay Westerveld bir Fiji otelinde havluları
            tekrar kullanmaya çağıran "çevreyi koruyun" kartını gördüğünde icat etti.
            Otel aynı dönemde çevresel duyarlılıkla ilgisi olmayan bir genişleme
            inşaatı yürütüyordu. Westerveld'in tespiti basit ve kalıcıydı: çevreci
            jest, çoğu zaman çevreci olmayan bir kararı örtüyor.
          </p>
          <p>
            Kavram önce reklamcılık eleştirisi olarak yaygınlaştı, sonra ESG raporlama
            standartlarıyla birlikte kurumsal sürdürülebilirlik literatürüne girdi.
            Bugün AB'nin Green Claims Direktifi, ABD'nin FTC Green Guides'ı ve
            Türkiye'de Reklam Kurulu kararları greenwashing'i regülatif bir kategori
            olarak tanımlıyor.
          </p>
        </section>

        <section id="kaliplar" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            3. Yedi tipik kalıp
          </h2>
          <p>Pazarlama danışmanlığı TerraChoice'ın klasikleşmiş "yedi günah" listesinden uyarlama:</p>
          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Gizli ödünleşim:</strong> Bir özelliği öne çıkarıp ürünün
              asıl etkisini gizlemek. "Geri dönüştürülmüş plastikten" yapılmış
              tek kullanımlık ürün hâlâ tek kullanımlıktır.
            </li>
            <li>
              <strong>Kanıtsız iddia:</strong> "Doğa dostu", "yeşil",
              "sürdürülebilir kaynaklı" — hiçbir sayı, sertifika ya da
              metodoloji belirtmeden.
            </li>
            <li>
              <strong>Belirsiz dil:</strong> "Karbon nötr olacağız" — ne zaman,
              hangi kapsamda (Scope 1, 2, 3?), hangi referans yılına göre?
            </li>
            <li>
              <strong>Sahte sertifika:</strong> Şirketin kendisinin tasarladığı
              ya da hiçbir bağımsız denetimden geçmeyen logo ve mühürler.
            </li>
            <li>
              <strong>Daha az kötünün pazarlanması:</strong> "Diğer SUV'lara
              göre daha düşük emisyonlu SUV." Kategori zaten yüksek emisyonluysa
              kıyas anlamını yitirir.
            </li>
            <li>
              <strong>Yalan:</strong> Doğrulanamaz ya da açıkça yanlış iddialar
              ("100% organik" — ürünün yalnızca paketi).
            </li>
            <li>
              <strong>Karbon dengeleme bağımlılığı:</strong> Emisyonu azaltmak
              yerine başka birinin orman projesinden sertifika satın alıp
              "net sıfır" ilan etmek. Çoğu sertifikanın gerçek ek azaltım
              ürettiği bağımsız çalışmalarda tartışmalıdır.
            </li>
          </ol>
        </section>

        <section id="ornekler" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            4. Gerçek dünyadan örnekler
          </h2>
          <p>
            <strong>Otomotiv — "temiz dizel" skandalı (2015).</strong> Bir Alman
            üreticisi araçlarını "düşük emisyonlu" olarak pazarlarken yazılımla
            laboratuvar testlerini geçecek şekilde ayarlamıştı. Yol emisyonu
            reklamdakinden 40 katına kadar çıkıyordu. Sonuç: milyarlarca dolar
            ceza ve sektör için kalıcı bir greenwashing referansı.
          </p>
          <p>
            <strong>Hızlı moda — "conscious" koleksiyonu.</strong> Bir İsveç
            zincirinin "bilinçli" koleksiyonu, üretim hacmini düşürmek yerine
            yıllık koleksiyon sayısını artırarak satışını sürdürdü. Norveç
            Tüketici Kurumu 2019'da iddianın "yanıltıcı" olduğunu açıkladı.
          </p>
          <p>
            <strong>Petrol — "ötesi" pazarlaması.</strong> 2000'lerin başında
            büyük bir petrol şirketi kendini "beyond petroleum" olarak yeniden
            adlandırdı, sarı-yeşil çiçek logosunu öne çıkardı. Sermaye
            harcamasının %96'sı hâlâ fosil yakıttaydı.
          </p>
          <p>
            <strong>Bankacılık — "yeşil tahvil" tartışması.</strong> Bazı bankalar
            yeşil tahvil ihracını duyururken aynı kuruluşa fosil yakıt finansmanı
            sağlamayı sürdürdü. Net etki sıfır — hatta negatif olabilir.
          </p>
        </section>

        <section id="neden" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            5. Neden bu kadar yaygın?
          </h2>
          <p>
            Üç yapısal sebep:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>Talep var, denetim yok.</strong> Tüketici ve yatırımcı
              sürdürülebilirlik talep ediyor. Bu talebi karşılamak gerçek
              değişimden daha hızlı, daha ucuz: pazarlama bütçesi her zaman
              tedarik zinciri yeniden tasarımından küçüktür.
            </li>
            <li>
              <strong>Ölçüm çoğul ve gönüllü.</strong> ESG çerçeveleri çoktur,
              karşılaştırma zayıftır. Aynı şirket farklı çerçevede farklı
              skorlar alır. Bu belirsizlik, seçici raporlamayı mümkün kılar.
            </li>
            <li>
              <strong>Düzenleyici gecikme.</strong> Reklamcılık mevzuatı yetişene
              kadar yeni bir iddia kalıbı doğar. AB'nin Green Claims Direktifi
              gibi araçlar geç, parçalı ve sınır ötesi uygulamada zayıf kalıyor.
            </li>
          </ul>
          <p>
            Bu yapısal arka plan benim "<Link to="/sozluk" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">washing ekonomisi</Link>"
            dediğim daha geniş bir örüntünün parçası — yalnızca yeşil değil,
            sosyal (pinkwashing, rainbow-washing), etik (AI ethics-washing) ve
            insani (humane-washing) versiyonları da var.
          </p>
        </section>

        <section id="kontrol-listesi" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            6. Tüketici kontrol listesi
          </h2>
          <p>Bir ürünün veya markanın iddiasını test etmek için beş soru:</p>
          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>İddianın arkasında sayı var mı?</strong> "Daha az emisyon"
              değil, "2019'a göre %32 daha az Scope 1 + 2 emisyon".
            </li>
            <li>
              <strong>Sertifikayı kim veriyor?</strong> B Corp, Cradle to Cradle,
              FSC, GOTS gibi bağımsız sistemler ile şirketin kendi logosu farklıdır.
            </li>
            <li>
              <strong>İddia ürünün ne kadarını kapsıyor?</strong> "Geri dönüştürülmüş
              malzemeden" — yüzde kaç? Yalnızca etiket mi, ürünün gövdesi mi?
            </li>
            <li>
              <strong>Toplam etki azalıyor mu?</strong> Bir kategoride iyileşme
              başka bir kategoride bozulmayı (su tüketimi, biyoçeşitlik) örtüyor olabilir.
            </li>
            <li>
              <strong>Üretim hacmi ne yapıyor?</strong> "Daha sürdürülebilir"
              ürünleri her yıl daha fazla üretiyorsa net etki çoğu zaman negatiftir.
            </li>
          </ol>
        </section>

        <section id="profesyonel" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            7. Profesyonel için: nasıl kaçınılır?
          </h2>
          <p>
            Sürdürülebilirlik iletişimi yazıyorsanız, üç kural:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>Kararın iletişimden önce gelmesi.</strong> Önce enerji karması,
              tedarik zinciri ya da ürün tasarımı değişsin; pazarlama bunun
              <em> sonrasında</em> yazılsın. Tersi yapıldığında neredeyse
              kaçınılmaz olarak greenwashing'e düşersiniz.
            </li>
            <li>
              <strong>Kapsam ve referans yılı her cümlede.</strong> "Karbon nötr"
              tek başına anlamsız. "2019 baz alınarak 2030'a kadar Scope 1, 2 ve
              kategori 1-11 Scope 3 emisyonlarında %50 mutlak azaltım" denetlenebilir.
            </li>
            <li>
              <strong>Dengeleme sertifikalarına bağımlılığı azaltın.</strong>
              Önce azalt, sonra geri kalan için dengele — ve dengelemenin
              metodolojisini şeffaf yayımla.
            </li>
          </ul>
        </section>

        <section id="ai" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            8. AI çağında yeni greenwashing
          </h2>
          <p>
            Son üç yılın en hızlı büyüyen greenwashing varyantı "AI iklim için"
            söylemi. Model eğitimi gigavatlarca elektrik, milyonlarca litre
            soğutma suyu ve devasa mineral tüketimi demektir. Bu maliyetler
            görünmez kalırken, AI ile yapılan tekil optimizasyonlar ("akıllı
            şebeke", "iklim modelleri") öne çıkarılıyor.
          </p>
          <p>
            Doğru soru şu: <em>hangi modeli, kim için, hangi enerji karmasıyla
            çalıştırıyoruz?</em> Bu çerçeve olmadan "AI sürdürülebilirlik için
            bir araç" cümlesi anlamlı olamıyor. Daha geniş tartışma için:{" "}
            <Link to="/yapay-zeka-toplum" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              Yapay Zekâ ve Toplum
            </Link>{" "}
            ve{" "}
            <Link to="/yazilar/surdurulebilirlik-nedir-rehberi" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              Sürdürülebilirlik Nedir — Bir Eleştiri
            </Link>.
          </p>
        </section>

        <section id="sss" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            9. Sıkça sorulanlar
          </h2>
          <dl className="space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="font-serif text-lg md:text-xl text-ink mb-2">{f.q}</dt>
                <dd className="text-ink/80">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-16 pt-8 border-t border-ink/10 text-sm text-ink/60">
          <p>
            Bu rehber{" "}
            <Link to="/yazilar/surdurulebilirlik-nedir-rehberi" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              Sürdürülebilirlik Nedir?
            </Link>{" "}
            yazısının bir devamı olarak okunabilir. Sözlük girdileri için{" "}
            <Link to="/sozluk" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              Sözlük
            </Link>{" "}
            sayfasına bakabilirsiniz.
          </p>
        </div>
      </div>
        </article>
      </PageSection>
    </>
  );
}
